/**
 * SaludView -- LA SALA DE MAQUINAS.
 *
 * MISION SALUD-TABLERO-V1-001, tarea SALUD-UI-T2.
 *
 * AUDIENCIA: JOSSE, el dueno del sistema, en escritorio. La pregunta que
 * responde es una sola: "esto de verdad esta funcionando, o solo lo parece?".
 * NO es para clientes ni colaboradores, y por eso puede decir cosas incomodas
 * sin suavizarlas.
 *
 * POR QUE VISTA NUEVA Y NO UNA SECCION EN AgentsDashboardView (gate regla E,
 * bitacora #9302): la existente responde QUE PASA AHORA (estado live) y ya
 * lleva tres capas de spec encima (KPI v1, paginacion v2.1, LOOP v3, 970
 * lineas). Esta responde SI ESTO FUNCIONA EN EL TIEMPO. Y el argumento que
 * decidio: este tablero MIDE los archivos-dios del panel; construirlo dentro
 * de uno lo habria hecho aparecer en su propia lista de problemas.
 *
 * FUTURISTA PERO HONESTO: hay brillo y animacion, pero ni un numero inventado.
 * Si un dato no llego, la tarjeta lo dice y explica por que.
 */

import { SaludApiClient } from '../../infrastructure/api/SaludApiClient.js';
import { salaDeVigentes } from '../../application/salud/vigentes.js';
import { leerClientes, DIMENSIONES, fraccionDe, sinDatoDe, estadoDelResumen } from '../../application/salud/clientes.js';
import { leerCumplimiento } from '../../application/salud/cumplimiento.js';
import { diasDeLaArquitectura, estadoDeFrescura, fraseDeCuando, medidoDelPie } from '../../application/salud/frescura.js';
import { caraDelIndicador } from '../../application/salud/caraDelIndicador.js';
import { renderChequeosSalud } from '../shared/NxChequeosSalud.js';
import { renderCapasArquitectura } from '../shared/NxCapasArquitectura.js';
import { resumenDeCapas, porQueNoHay } from '../../application/salud/capasArquitectura.js';

/** La fecha de hoy en Bogota (YYYY-MM-DD), por si la foto del tablero no la trae. */
const hoyEnBogota = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota' }).format(new Date());

// --- paleta de la sala (oscura, con acentos frios) --------------------------
const C = {
  bien: '#3fb950', ojo: '#d29922', mal: '#f85149',
  frio: '#58a6ff', neon: '#7ee7ff', violeta: '#c9a9ff',
  texto: '#e1e4e8', suave: '#8b949e', tenue: '#6e7681',
  panel: '#0d1117', borde: '#30363d'
};

// Deuda 4: este escape (no cambiaba la comilla simple) es ahora el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
const nfmt = (n) => (n === null || n === undefined || Number.isNaN(n) ? '—' : Number(n).toLocaleString('es-CO'));
const pct = (n) => (n === null || n === undefined || Number.isNaN(n) ? '—' : String(Number(n).toFixed(1)).replace(/\.0$/, '') + '%');

/** Semaforo por valor. Sin dato NO es rojo: es gris, que es distinto. */
function semaforo(valor, bienSi, ojoSi) {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return { color: C.tenue, palabra: 'sin dato' };
  if (bienSi(valor)) return { color: C.bien, palabra: 'bien' };
  if (ojoSi(valor)) return { color: C.ojo, palabra: 'ojo' };
  return { color: C.mal, palabra: 'mal' };
}

/** Anillo del reactor: SVG puro, sin librerias. El brillo lo da el filtro. */
function anillo(porcentaje, color, etiquetaGrande) {
  const p = (porcentaje === null || porcentaje === undefined || Number.isNaN(porcentaje))
    ? 0 : Math.max(0, Math.min(100, porcentaje));
  const R = 52;
  const CIRC = 2 * Math.PI * R;
  const relleno = (p / 100) * CIRC;
  return '<svg viewBox="0 0 130 130" style="width:130px;height:130px;display:block">'
    + '<circle cx="65" cy="65" r="' + R + '" fill="none" stroke="' + C.borde + '" stroke-width="9"/>'
    + '<circle cx="65" cy="65" r="' + R + '" fill="none" stroke="' + color + '" stroke-width="9"'
    + ' stroke-linecap="round" stroke-dasharray="' + relleno + ' ' + CIRC + '"'
    + ' transform="rotate(-90 65 65)"'
    + ' style="filter:drop-shadow(0 0 6px ' + color + ');transition:stroke-dasharray .9s ease-out"/>'
    + '<text x="65" y="70" text-anchor="middle" fill="' + color + '" font-size="23" font-weight="700"'
    + ' font-family="monospace">' + esc(etiquetaGrande) + '</text>'
    + '</svg>';
}

/**
 * Una barra por repo con lo que el arbol conoce de su codigo (Ares #40070). En gris como el resto del dial: sin meta
 * aprobada no hay bien ni mal. casa = conocidoDeLaCasa de application/salud/capasArquitectura.js.
 */
function barrasDeLaCasa(casa) {
  return '<div data-arq-casa style="display:flex;flex-direction:column;gap:4px;width:100%;margin-bottom:8px">'
    + casa.repos.map((r) => '<div data-arq-repo="' + esc(r.repo) + '" style="display:flex;align-items:center;gap:6px;font-size:0.875rem">'
      + '<span style="flex:0 0 84px;color:' + C.suave + ';text-align:left;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + esc(r.nombre) + '</span>'
      + '<span style="flex:1;height:6px;background:' + C.borde + ';border-radius:3px;overflow:hidden"><span style="display:block;height:100%;width:' + Math.max(0, Math.min(100, r.pct)) + '%;background:' + C.tenue + '"></span></span>'
      + '<span style="flex:0 0 52px;text-align:right;color:' + C.texto + '">' + pct(r.pct) + '</span></div>').join('')
    + (casa.sinContar ? '<div style="font-size:0.875rem;color:' + C.suave + '">' + nfmt(casa.sinContar) + ' repo(s) sin su cuenta de archivos: no entran en la suma.</div>' : '')
    + '</div>';
}

/** Los 3 reactores de arriba: una pregunta en cristiano cada uno. */
// LA EDAD DE LA FOTO DE ARQUITECTURA sale de la fecha de la foto, no de `generado`, que es la hora de la respuesta:
// con `generado` el dial decia "Medido hoy" sobre la foto del 8-sep (SALA-ARQ-VISTA-T1). Desde Ares #38227 la foto del
// dial 3 es la de capas (capasRes). Reglas en application/salud/frescura.js.
function reactores(d, capasRes) {
  const ag = d.agentes || [];
  const cola = d.cola || [];

  // 1. Trabajan solos? -> autonomia GLOBAL PONDERADA (SALUD-FIX-T4, bitacora #9548).
  //
  // Antes esto era el promedio simple de los porcentajes de cada agente, y por eso el
  // panel decia 48% cuando el informe de Ares decia 29%: los dos bien calculados, el
  // mismo indicador con dos definiciones. El promedio simple le da el mismo peso a un
  // agente que arranco 2 veces que a uno que arranco 30, asi que uno casi inactivo con
  // 100% empuja el numero como si mandara igual.
  //
  // El ponderado suma primero y divide despues: de TODOS los arranques, cuantos no los
  // pidio nadie. El promedio por agente no se pierde: sigue en la tabla de abajo, que
  // es donde esa lectura si corresponde.
  const totArranques = ag.reduce((s, a) => s + (a.arranques || 0), 0);
  const totAssigns   = ag.reduce((s, a) => s + (a.assignsRecibidos || 0), 0);
  // Math.max porque un agente puede recibir mas assigns que arranques (le piden mas de
  // lo que alcanza a arrancar): sin esto el indicador podria salir negativo.
  const propios = Math.max(0, totArranques - totAssigns);
  const autonomia = totArranques > 0 ? (propios / totArranques) * 100 : null;
  const sem1 = semaforo(autonomia, (v) => v >= 60, (v) => v >= 30);

  // 2. La cola dice la verdad? -> zombis global + pendientes viejas.
  const totPend = cola.reduce((s, c) => s + (c.pendientes || 0), 0);
  const totZomb = cola.reduce((s, c) => s + (c.zombisConDone || 0), 0);
  const tot30 = cola.reduce((s, c) => s + (c.masDe30d || 0), 0);
  const zombPct = totPend > 0 ? (totZomb / totPend) * 100 : null;
  // EL NUMERO GRANDE Y LA PALABRA TIENEN QUE HABLAR DE LO MISMO (Argy-Q,
  // TABLERO-AUDIT-T1 #11585, veredicto: "se lee al reves").
  //
  // El anillo mostraba 100-zombPct -- o sea el 85% SANO -- y debajo ponia la
  // palabra del semaforo calculada sobre zombPct, el 15% podrido. En pantalla
  // quedaba "85%" con "MAL" debajo, y cualquiera lo lee como "85% mal", que es
  // exactamente al reves de lo que pasa.
  //
  // Ahora el semaforo se calcula sobre LO SANO, que es lo que se pinta.
  const sanoPct = zombPct === null ? null : 100 - zombPct;
  // LA PALABRA TIENE QUE SER PROPORCIONAL AL NUMERO (Q, #11861, corrigiendose
  // a si mismo DESPUES DE VER el tablero).
  //
  // Arregle que el semaforo calificara lo mismo que se pinta, y eso quedo bien.
  // Pero en pantalla el resultado se veia IGUAL que el bug original: un 85%
  // grande, el anillo rojo y MAL debajo, tambien rojo. Un ojo que pasa sin
  // detenerse lee "85% mal". El defecto tenia dos capas y leyendo codigo solo
  // se ve una.
  //
  // Con el umbral en `> 85`, un 85 EXACTO caia en rojo por un signo. Y aunque
  // no cayera: llamar MAL a una cola sana en 85 de cada 100 es desproporcionado,
  // y es lo que hace que el numero y la palabra se peleen en la misma tarjeta.
  const sem2 = semaforo(sanoPct, (v) => v >= 95, (v) => v >= 80);

  // 3. La arquitectura aguanta? -> las capas (Ares #38227). Antes leia archivos_dios y deriva_catalogo, cuya ultima foto
  // era del 2-sep: Josse veia SIN DATO. Ahora: el repo menos conocido (capa 0) y la basura de la capa 1, de la foto de
  // capas. Sin meta aprobada va en gris, nunca verde; el detalle esta en la grilla de capas.
  // SALA-ARQ-VISTA-T1 sigue: la edad sale de la fecha de la foto, y mas de 7 dias es VENCIDO en rojo.
  const capas = resumenDeCapas(capasRes?.ok ? capasRes.data : [], d.foto?.hoy || hoyEnBogota());
  const diasArq = capas.dias;
  const frescuraArq = capas.frescura;
  const sem3 = frescuraArq === 'vencido' && capas.peor !== null
    ? { color: C.mal, palabra: 'vencido' }
    : { color: C.tenue, palabra: capas.peor === null ? 'sin dato' : 'sin meta' };

  function tarjeta(titulo, sem, anilloPct, textoGrande, frase, bajoElAnillo) {
    return '<div style="flex:1 1 260px;min-width:260px;background:' + C.panel + ';border:1px solid ' + C.borde + ';'
      + 'border-radius:14px;padding:18px;display:flex;flex-direction:column;align-items:center;gap:10px;'
      + 'box-shadow:inset 0 0 40px ' + sem.color + '11">'
      + '<div style="font-size:0.875rem;color:' + C.texto + ';font-weight:700">' + titulo + '</div>'
      + anillo(anilloPct, sem.color, textoGrande)
      + (bajoElAnillo ? '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:-4px">' + esc(bajoElAnillo) + '</div>' : '')
      + '<div style="font-size:0.875rem;color:' + sem.color + ';text-transform:uppercase;letter-spacing:.08em;font-weight:700">' + esc(sem.palabra) + '</div>'
      + '<div style="font-size:0.875rem;color:' + C.suave + ';text-align:center;line-height:1.5">' + frase + '</div>'
      + '</div>';
  }

  const f1 = autonomia === null
    ? 'Todavia no hay datos de autonomia.'
    : 'De <strong style="color:' + C.texto + '">' + nfmt(totArranques) + '</strong> trabajos arrancados, '
      + '<strong style="color:' + C.texto + '">' + nfmt(totAssigns) + '</strong> se los pidio Ares. '
      + 'Los otros <strong style="color:' + C.texto + '">' + nfmt(propios) + '</strong> los empezaron ellos solos.';
  // La frase arranca por lo SANO y despues nombra el costo, en ese orden: es
  // lo que evita que el porcentaje grande se lea al reves.
  const f2 = zombPct === null
    ? 'Todavia no hay datos de cola.'
    : 'De <strong style="color:' + C.texto + '">' + nfmt(totPend) + '</strong> tareas pendientes, '
      + '<strong style="color:' + C.texto + '">' + nfmt(totPend - totZomb) + '</strong> lo estan de verdad. '
      + 'Las otras <strong style="color:' + C.texto + '">' + nfmt(totZomb) + '</strong> ya estaban hechas y nadie las cerro. '
      + 'Y <strong style="color:' + C.texto + '">' + nfmt(tot30) + '</strong> llevan mas de 30 dias sin moverse.';
  // LA FOTO DICE DE CUANDO ES, y avisa si esta vieja (pedido de Q: el numero
  // salia sin fecha en pantalla y era del 2-sep).
  const cuando = ' ' + fraseDeCuando({ arquitectura: capas.fecha, hoy: d.foto?.hoy || hoyEnBogota() });
  const vieja = capas.fotos === 0 ? '' : frescuraArq === 'vencido'
    ? '<div data-arq-frescura="vencido" style="color:' + C.mal + ';font-size:0.875rem;margin-top:4px;font-weight:700">'
      + '&#9888;&#65039; VENCIDO: esta foto tiene ' + diasArq + ' dias (mas de 7). No es el estado de hoy.</div>'
    : frescuraArq === 'vieja'
      ? '<div data-arq-frescura="vieja" style="color:' + C.ojo + ';font-size:0.875rem;margin-top:4px">'
        + '&#9888;&#65039; Esta foto tiene ' + diasArq + ' dias: el numero puede haber cambiado.</div>'
      : frescuraArq === 'sin_fecha'
        ? '<div data-arq-frescura="sin_fecha" style="color:' + C.ojo + ';font-size:0.875rem;margin-top:4px">&#9888;&#65039; Sin fecha: no se puede saber si esto sigue siendo cierto.</div>'
        : '';
  const f3 = capas.fotos === 0
    ? 'Todavía no hay foto de capas. ' + esc(porQueNoHay(capasRes?.ok ? 'VACIO' : capasRes?.motivo))
    : (capas.peor === null
      ? 'Ningún repo midió todavía cuánto de su código conocemos.'
      // Ares #40070: el numero grande es TODA la casa; cada repo en su barra; el menos conocido, dicho en texto.
      : (capas.casa ? barrasDeLaCasa(capas.casa) : '')
        + 'El repo que menos conocemos es <strong style="color:' + C.texto + '">' + esc(capas.peor.nombre) + '</strong>: el árbol nombra el '
        + '<strong style="color:' + C.texto + '">' + pct(capas.peor.valor) + '</strong> de su código.')
      + (capas.basura === null ? '' : ' Basura de la capa 1 en ' + nfmt(capas.fotos) + (capas.fotos === 1 ? ' repo' : ' repos') + ': '
        + '<strong style="color:' + C.texto + '">' + nfmt(capas.basura) + '</strong>'
        + (capas.sinContar ? ' (sin contar ' + nfmt(capas.sinContar) + ' números que no se midieron)' : '') + '.')
      + ' Sin meta aprobada: va en gris. El detalle, en la grilla de capas.' + esc(cuando) + vieja;

  return '<div style="display:flex;gap:14px;flex-wrap:wrap;margin-bottom:22px">'
    + tarjeta('&#9889; Trabajan solos?', sem1, autonomia, pct(autonomia), f1)
    // El subtitulo del anillo dice CUANTAS son las que ensucian: el numero
    // grande responde "cuanto puedo creerle" y el chico, "que hay que arreglar".
    // Asi la palabra tiene a que agarrarse sin leer el parrafo.
    + tarjeta('&#128220; La cola dice la verdad?', sem2, sanoPct,
      sanoPct === null ? '—' : pct(sanoPct), f2,
      totZomb > 0 ? nfmt(totZomb) + ' sin cerrar' : null)
    // El anillo es lo conocido de TODA la casa (suma de conocidos / suma de archivos de los repos medidos), no el minimo
    // (Ares #40070: el 0,6 de la web se leia como el total). Sin la suma (ningun repo trae conocidos), el anillo dice —.
    + tarjeta('&#127959; La arquitectura aguanta?', sem3, capas.casa ? capas.casa.pct : null,
      capas.casa ? pct(capas.casa.pct) : '—', f3,
      capas.casa ? nfmt(capas.casa.conocidos) + ' de ' + nfmt(capas.casa.total) + ' archivos, ' + nfmt(capas.casa.repos.length) + ' repos' : null)
    + '</div>';
}

/** Fila por agente. El retrabajo alto NO es malo por si solo: se dice. */
function filaAgente(a) {
  const semEv = semaforo(a.evidenciaPct, (v) => v >= 90, (v) => v >= 70);
  const semAu = semaforo(a.autonomiaPct, (v) => v >= 60, (v) => v >= 30);
  function barra(v, color) {
    const ancho = (v === null || v === undefined || Number.isNaN(v)) ? 0 : Math.max(0, Math.min(100, v));
    return '<div style="height:6px;background:' + C.borde + ';border-radius:3px;overflow:hidden;margin-top:4px">'
      + '<div style="height:100%;width:' + ancho + '%;background:' + color + ';box-shadow:0 0 8px ' + color
      + ';transition:width .8s ease-out"></div></div>';
  }
  const notaRetrabajo = (a.retrabajoPct !== null && a.retrabajoPct >= 30)
    ? '<div style="margin-top:8px;font-size:0.875rem;color:' + C.violeta + ';line-height:1.5">'
      + '&#8635; Rehacer mucho no es lo mismo que equivocarse: este numero tambien sube cuando alguien se corrige a si mismo antes de entregar.</div>'
    : '';
  function celda(rotulo, valorTexto, color, valorBarra, conBarra) {
    return '<div><div style="font-size:0.875rem;color:' + C.suave + '">' + rotulo + '</div>'
      + '<div style="font-size:1.05rem;color:' + color + ';font-weight:700">' + valorTexto + '</div>'
      + (conBarra ? barra(valorBarra, color) : '') + '</div>';
  }
  return '<div style="background:' + C.panel + ';border:1px solid ' + C.borde + ';border-left:3px solid ' + C.frio + ';border-radius:10px;padding:14px">'
    + '<div style="display:flex;justify-content:space-between;align-items:baseline;gap:8px;flex-wrap:wrap">'
    + '<span style="font-family:monospace;font-size:0.875rem;color:' + C.neon + ';font-weight:700">' + esc(a.agente) + '</span>'
    + '<span style="font-size:0.875rem;color:' + C.suave + '">' + nfmt(a.entregas) + ' entregas &middot; ' + nfmt(a.hallazgos) + ' hallazgos</span>'
    + '</div>'
    + '<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(120px,1fr));gap:12px;margin-top:12px">'
    + celda('Arranca solo', pct(a.autonomiaPct), semAu.color, a.autonomiaPct, true)
    + celda('Entrega con pruebas', pct(a.evidenciaPct), semEv.color, a.evidenciaPct, true)
    + celda('Rehace', pct(a.retrabajoPct), C.violeta, a.retrabajoPct, true)
    + celda('Se traba', nfmt(a.bloqueos), C.texto, null, false)
    + '</div>' + notaRetrabajo + '</div>';
}

/** Arquitectura: los numeros incomodos, cada uno con su explicacion. */
function bloqueArquitectura(items) {
  if (!items.length) {
    return '<div style="padding:14px;color:' + C.suave + ';font-size:0.875rem">No llego el detalle de arquitectura.</div>';
  }
  // AQUI HABIA UN DICCIONARIO DE EXPLICACIONES ESCRITO A MANO, y lo saco por
  // una razon que se puede contar con numeros (SALUD-FUENTE-T2).
  //
  // Explicaba siete claves: archivos_dios, archivo_mayor, archivos_huerfanos,
  // deriva_catalogo, archivos_totales, lineas_totales y capabilities_activas.
  // La foto de arquitectura manda hoy otras ocho: archivos_sueltos_raiz,
  // carpetas_fuera_de_radar, mb_fuera_de_repo, repos_en_casa,
  // repos_fuera_de_main, repos_sin_readme, repos_sin_remoto y
  // worktrees_huerfanos. El cruce da CERO: el diccionario explicaba siete
  // claves que ya no existen, y las ocho que existen caian en la cadena vacia.
  //
  // O sea que estas tarjetas llevaban tiempo saliendo SIN explicacion, y nadie
  // lo vio porque el hueco vacio se ve igual que un texto corto.
  //
  // Y la explicacion SI viajaba: cada fila de la foto trae detalle.significa en
  // cristiano. La vista ya lo leia, pero lo pintaba abajo y chiquito, como nota
  // al pie, mientras el sitio de la explicacion quedaba en blanco. Ahora va
  // donde corresponde, y si un dia no viene se DICE en vez de dejar el hueco.
  return items.map(function (x) {
    const baja = x.direccionMeta === 'bajar';
    const color = baja ? (x.valor > 10 ? C.mal : (x.valor > 3 ? C.ojo : C.bien)) : C.frio;
    let extra = '';
    const det = x.detalle;
    const significa = det && typeof det === 'object' && typeof det.significa === 'string'
      ? det.significa : null;
    if (det && typeof det === 'object') {
      if (Array.isArray(det.top5)) extra = det.top5.slice(0, 5).map(esc).join(' &middot; ');
      else if (Array.isArray(det.lista)) extra = det.lista.slice(0, 7).map(esc).join(' &middot; ');
      else if (det.archivo) extra = esc(det.archivo);
      // significa ya NO se usa aqui: subio al sitio de la explicacion.
      // Lo que queda en 'extra' son las listas, que son detalle y no explicacion.
    }
    return '<div style="background:' + C.panel + ';border:1px solid ' + C.borde + ';border-radius:10px;padding:13px">'
      + '<div style="display:flex;justify-content:space-between;align-items:baseline;gap:10px">'
      + '<span style="font-size:0.875rem;color:' + C.texto + ';font-weight:600">' + esc(String(x.clave).replace(/_/g, ' ')) + '</span>'
      + '<span style="font-size:1.25rem;color:' + color + ';font-weight:700;font-family:monospace">' + nfmt(x.valor)
      + '<span style="font-size:0.875rem;color:' + C.tenue + ';margin-left:4px">' + esc(x.unidad) + '</span></span>'
      + '</div>'
      + '<div style="font-size:0.875rem;color:' + (significa ? C.suave : C.tenue) + ';margin-top:6px;line-height:1.5">'
      + esc(significa || 'Este numero todavia no viene con su explicacion.') + '</div>'
      + (extra ? '<div style="font-size:0.875rem;color:' + C.tenue + ';margin-top:6px;line-height:1.5">' + extra + '</div>' : '')
      + '</div>';
  }).join('');
}

/** Tendencia. Con un solo dia NO se finge una linea: se dice. */
function bloqueTendencia(tend) {
  const dias = Array.from(new Set(tend.map((t) => t.fecha))).filter(Boolean).sort();
  if (dias.length === 0) {
    return '<div style="padding:14px;color:' + C.suave + ';font-size:0.875rem;background:' + C.panel
      + ';border:1px dashed ' + C.borde + ';border-radius:10px">Todavia no hay historico para dibujar la tendencia.</div>';
  }
  if (dias.length === 1) {
    return '<div style="padding:16px;background:' + C.panel + ';border:1px solid ' + C.borde
      + ';border-left:3px solid ' + C.frio + ';border-radius:10px">'
      + '<div style="font-size:0.875rem;color:' + C.texto + ';font-weight:700">Primer dia medido &middot; ' + esc(dias[0]) + '</div>'
      + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:6px;line-height:1.6">'
      + 'Hoy es el punto de partida: hay <strong style="color:' + C.texto + '">' + nfmt(tend.length)
      + '</strong> mediciones de un solo dia. La linea aparece cuando haya un segundo dia. '
      + 'Dibujar una tendencia con un solo punto seria inventarla.</div></div>';
  }
  const porClave = new Map();
  for (const t of tend) {
    const k = t.ambito + '/' + t.clave + (t.sujeto ? ' · ' + t.sujeto : '');
    if (!porClave.has(k)) porClave.set(k, []);
    porClave.get(k).push(t);
  }
  return Array.from(porClave.entries()).slice(0, 12).map(function (par) {
    const k = par[0];
    const s = par[1].filter((x) => x.valor !== null).sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)));
    if (s.length < 2) return '';
    const vals = s.map((x) => x.valor);
    const min = Math.min.apply(null, vals);
    const max = Math.max.apply(null, vals);
    const rango = (max - min) || 1;
    const pts = s.map((x, i) => ((i / (s.length - 1)) * 100) + ',' + (30 - ((x.valor - min) / rango) * 26)).join(' ');
    const sube = vals[vals.length - 1] > vals[0];
    const trazo = sube ? C.neon : C.violeta;
    return '<div style="background:' + C.panel + ';border:1px solid ' + C.borde + ';border-radius:10px;padding:12px">'
      + '<div style="display:flex;justify-content:space-between;align-items:baseline">'
      + '<span style="font-size:0.875rem;color:' + C.suave + '">' + esc(k) + '</span>'
      + '<span style="font-size:0.875rem;color:' + C.texto + ';font-weight:700;font-family:monospace">' + nfmt(vals[vals.length - 1]) + '</span></div>'
      + '<svg viewBox="0 0 100 32" preserveAspectRatio="none" style="width:100%;height:34px;margin-top:6px">'
      + '<polyline points="' + pts + '" fill="none" stroke="' + trazo + '" stroke-width="1.6"'
      + ' vector-effect="non-scaling-stroke" style="filter:drop-shadow(0 0 3px ' + trazo + ')"/></svg>'
      + '<div style="font-size:0.875rem;color:' + C.tenue + '">' + esc(s[0].fecha) + ' &rarr; ' + esc(s[s.length - 1].fecha)
      + ' &middot; ' + s.length + ' dias</div></div>';
  }).join('');
}

/** Cuando el dato NO llego: se dice por que, y que se puede hacer. */
function pantallaSinDatos(motivo, detalle) {
  const MENSAJE = {
    NO_PUBLICADO: ['El tablero todavia no tiene de donde leer',
      'El endpoint <code>GET /api/v1/salud/tablero</code> aun no esta publicado. La pantalla ya esta lista y se enciende sola el dia que exista, sin tocar nada mas.'],
    SIN_SESION: ['Tu sesion no alcanza para ver esto', 'La Sala de Maquinas es del dueno del sistema.'],
    PROHIBIDO: ['Esta sala no es para esta cuenta', 'Solo el dueno del sistema entra aca.'],
    SIN_RED: ['No pudimos hablar con la API', 'Puede ser la conexion. Reintenta en un momento.'],
    ERROR_API: ['La API respondio con un error', 'Abajo esta el detalle tal como llego.']
  };
  const m = MENSAJE[motivo] || ['No se pudo cargar el tablero', ''];
  return '<div style="max-width:620px;margin:40px auto;padding:26px;background:' + C.panel + ';border:1px solid ' + C.borde
    + ';border-left:3px solid ' + C.ojo + ';border-radius:12px;text-align:center">'
    + '<div style="font-size:2.2rem;margin-bottom:10px">&#128268;</div>'
    + '<div style="font-size:1rem;color:' + C.texto + ';font-weight:700">' + esc(m[0]) + '</div>'
    + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:10px;line-height:1.6">' + m[1] + '</div>'
    + (detalle ? '<div style="font-size:0.875rem;color:' + C.tenue + ';margin-top:12px;font-family:monospace">' + esc(detalle) + '</div>' : '')
    + '<div style="font-size:0.875rem;color:' + C.tenue + ';margin-top:16px">'
    + 'No se inventa ningun numero: preferimos la pantalla vacia y honesta.</div></div>';
}

// --- la vista ---------------------------------------------------------------

/**
 * LOS SEIS NUMEROS DEL TRABAJO, la contraparte de panel del rincon del Gremio
 * (IND-VIVO-T2). Josse lo dijo asi: "arcade gamificado, panel indicadores y
 * fuentes para verificar". Aqui manda la SEGUNDA mitad: cada numero con de
 * donde sale, para poder comprobarlo.
 */
const NOMBRE_SEIS = {
  nos_conocemos: 'Nos conocemos',
  reusamos: 'Reusamos lo que ya hay',
  promesa: 'Cumplimos lo prometido',
  embudo: 'Lo que esta trabado',
  aprendemos: 'Aprendemos de los errores',
  como_voy: 'Cada quien sabe como va'
};

/**
 * El numero grande de cada indicador y la frase que lo acompana.
 *
 * PUNTO 7 DE PANTALLA-v1, que nacio de un error mio en esta misma pantalla: lo
 * que se muestra grande y lo que se dice al lado tienen que hablar del MISMO
 * dato. Por eso la frase es SIEMPRE la cuenta de donde sale el numero grande,
 * nunca otra medida que quede cerca.
 */
/**
 * Horas en la unidad del dueno. 13410 horas no dicen nada; 559 dias gritan.
 *
 * Nace de la nota de Ares (#12059): el PR mas viejo sin dictamen es un fosil
 * de hace ano y medio, y arrastra el numero del embudo. Un numero absurdo en
 * la unidad del dueno se investiga; en la unidad de la maquina se promedia y
 * pasa desapercibido.
 */
function enCristiano(horas) {
  if (horas === null || horas === undefined) return null;
  const h = Number(horas);
  if (!Number.isFinite(h)) return null;
  if (h < 48) return Math.round(h) + (Math.round(h) === 1 ? ' hora' : ' horas');
  const d = Math.round(h / 24);
  return d + (d === 1 ? ' dia' : ' dias');
}

/**
 * LAS DOS EDADES DEL EMBUDO, cada una en su linea.
 *
 * El del trabajo de AHORA es el que se puede mover esta semana. El fosil se
 * nombra aparte para que no parezca que se escondio: sigue ahi y sigue siendo
 * un PR abierto, pero no puede hablar por el trabajo de hoy.
 *
 * Si solo llega el viejo -- porque la foto es anterior a IND-VIVO-T1b -- se
 * pinta ese y se dice que incluye el fosil, en vez de callar la diferencia.
 */
function lineasDelMasViejo(n) {
  const num = n || {};
  const ahora = enCristiano(num.mas_viejo_del_trabajo_actual_horas);
  const conFosil = enCristiano(num.mas_viejo_horas);
  if (!ahora && !conFosil) return '';
  const linea = (color, txt) =>
    '<div style="font-size:0.875rem;color:' + color + ';margin-top:3px">' + txt + '</div>';
  if (!ahora) {
    return linea(C.ojo, 'El mas viejo lleva ' + esc(conFosil)
      + ' sin dictamen, y ese numero incluye un PR fosil: mira ese aparte.');
  }
  let html = linea(C.ojo, 'Del trabajo de ahora, el mas viejo lleva ' + esc(ahora) + ' sin dictamen.');
  // El fosil solo se nombra si de verdad es OTRO numero. Repetirlo cuando
  // coinciden seria inventar un problema que hoy no existe.
  if (conFosil && conFosil !== ahora) {
    html += linea(C.tenue, 'Y hay un PR fosil de hace ' + esc(conFosil)
      + ': cuenta como abierto, pero no es el trabajo de esta semana.');
  }
  return html;
}


/** Una fila por indicador: el numero, lo que significa, y de donde sale. */
function filaIndicador(i) {
  const nombre = NOMBRE_SEIS[i.clave] || i.clave;
  // SIN DATOS NO ES CERO, y tampoco es rojo: es gris, que es lo que significa.
  const cara = i.sinDatos ? { grande: 'sin datos', frase: 'todavia no se ha tomado la foto' } : caraDelIndicador(i);
  const color = i.sinDatos ? C.tenue : C.texto;
  return '<div style="background:' + C.panel + ';border:1px solid ' + C.borde + ';border-radius:12px;padding:14px 16px;'
    + 'display:flex;gap:16px;align-items:flex-start;flex-wrap:wrap">'
    + '<div style="min-width:120px">'
    + '<div style="font-size:1.5rem;font-weight:700;color:' + color + ';line-height:1.1">' + esc(cara.grande) + '</div>'
    + (cara.frase ? '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:2px">' + esc(cara.frase) + '</div>' : '')
    + '</div>'
    + '<div style="flex:1 1 260px;min-width:0">'
    + '<div style="font-size:0.875rem;color:' + C.texto + ';font-weight:700">' + i.orden + '. ' + esc(nombre) + '</div>'
    // ESTAS DOS LINEAS ERAN "si viene, se pinta" A SECAS, y esta pantalla se
    // llama Sala de Maquinas justamente porque explica de que esta hecho cada
    // numero. Cuando no venian, la fila salia con el numero y el nombre y nada
    // mas, y Josse no tiene forma de saber si ese indicador no tiene meta o si
    // la API no la mando.
    //
    // Es el mismo hueco que acabo de tapar en la foto de arquitectura, veinte
    // lineas mas arriba: "el hueco vacio se ve igual que un texto corto". Y la
    // linea de la fuente, aqui abajo, ya lo hacia bien desde el primer dia.
    //
    // MEDIDO HOY, 9-sep, contra nexo_private.indicadores_trabajo(): los seis
    // traen explica, meta y fuente, asi que hoy no se pierde nada. Pero la
    // migracion que los arma solo tumba el despliegue si falta explica o
    // fuente -- esta en su RAISE, no en su comentario -- y meta no tiene quien
    // la vigile. El unico que se puede perder callado era el que aqui se
    // perdia mas callado.
    + '<div style="font-size:0.875rem;color:' + (i.explica ? C.suave : C.tenue) + ';margin-top:3px">'
    + (i.explica ? esc(i.explica) : 'Que significa: la API todavia no manda la explicacion.') + '</div>'
    + '<div style="font-size:0.875rem;color:' + C.tenue + ';margin-top:3px">Meta: '
    + (i.meta ? esc(i.meta) : 'la API todavia no manda la meta.') + '</div>'
    // EL MAS VIEJO DEL TRABAJO DE AHORA, Y EL FOSIL APARTE (SALUD-FUENTE-T2).
    //
    // Aqui se pintaba mas_viejo_horas, que incluye un PR de hace ano y medio y
    // arrastra el numero: la linea decia -el mas viejo lleva 558 dias-. Cierto
    // y a la vez inutil, porque no habla del trabajo de esta semana.
    //
    // La API ya manda los dos (IND-VIVO-T1b): mas_viejo_del_trabajo_actual_horas
    // es el de ahora y mas_viejo_horas incluye el fosil. Se pintan SEPARADOS: el
    // de ahora es el que se puede mover esta semana, y el fosil se nombra para
    // que no parezca que desaparecio.
    //
    // Se mide con la API, no con la funcion pelada: ese campo lo INYECTA
    // SaludTableroService desde la foto, asi que un psql a indicadores_trabajo()
    // no lo muestra y parece que no existe.
    + lineasDelMasViejo(i.numeros)
    // LA FUENTE, QUE ES LO QUE ESTA SECCION APORTA SOBRE EL GREMIO. Si no
    // llego, se dice: inventarla seria peor que no tenerla, porque Josse la
    // usaria para verificar y no le cuadraria.
    + '<div style="font-size:0.875rem;margin-top:6px;font-family:ui-monospace,monospace;color:'
    + (i.fuente ? C.frio : C.tenue) + ';overflow-wrap:anywhere">'
    + (i.fuente ? 'Sale de: ' + esc(i.fuente) : 'De donde sale: la API todavia no manda la fuente.')
    + '</div>'
    + '</div></div>';
}

/** La seccion entera, incluidos sus estados vacios. */
function bloqueLosSeis(res) {
  if (!res) {
    return '<div style="color:' + C.suave + ';font-size:0.875rem">Midiendo...</div>';
  }
  if (!res.ok) {
    // PUNTO 6 DE PANTALLA-v1: decir que pasa y que hacer, sin codigos internos.
    // Y por CAUSA: un mensaje unico para todo hace dudar de lo que si funciona.
    return '<div style="background:' + C.panel + ';border:1px solid ' + C.borde + ';border-radius:12px;padding:16px">'
      + '<div style="font-size:0.875rem;color:' + C.texto + ';font-weight:700">Todavia no puedo mostrar los seis numeros</div>'
      + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:6px">' + esc(res.detalle || '') + '</div>'
      + '<div style="font-size:0.875rem;color:' + C.tenue + ';margin-top:6px">'
      + 'No estan en cero: es que no llego la respuesta.</div></div>';
  }
  const d = res.data || {};
  if (!d.indicadores || !d.indicadores.length) {
    return '<div style="background:' + C.panel + ';border:1px solid ' + C.borde + ';border-radius:12px;padding:16px">'
      + '<div style="font-size:0.875rem;color:' + C.suave + '">La API respondio, pero todavia no manda ningun numero.</div></div>';
  }
  // LA EDAD DE LA FOTO (dictamen de Q #12076 sobre el Gremio). El mismo hueco
  // estaba aqui: SaludApiClient la traia y esta vista no la pintaba. Se nombra
  // el indicador porque solo el embudo sale de la foto guardada.
  const dias = d.fotoDiasAtras;
  const vieja = Number.isFinite(dias) && dias > 0
    ? '<div style="font-size:0.875rem;color:' + C.ojo + ';margin-bottom:8px">Ojo: &laquo;lo que esta trabado&raquo; sale de una foto de '
      + (dias === 1 ? 'ayer' : 'hace ' + dias + ' dias') + '.</div>'
    : '';
  const aviso = d.traeFuentes ? ''
    : '<div style="font-size:0.875rem;color:' + C.tenue + ';margin-bottom:8px">'
      + 'La fuente de cada numero todavia no viaja en la API. Pedida en bitacora #12017.</div>';
  return vieja + aviso
    + '<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));gap:12px">'
    + d.indicadores.map(filaIndicador).join('')
    + '</div>'
    + (d.corte ? '<div style="margin-top:10px;font-size:0.875rem;color:' + C.tenue + ';text-align:right">Medido ' + esc(d.corte) + '</div>' : '');
}

/**
 * EL CUMPLIMIENTO DE LAS REGLAS V2 (PROTOCOLOS-V2-T2 parte B). Una fila por Argy,
 * tres fracciones: cierres con las claves, WIP con checklist_leida, DONE con nodos.
 * El color lo decide application/salud/cumplimiento.js; 0 de 0 es gris, no rojo.
 */
function colorDeFraccion(f) {
  if (f.estado === 'verde') return C.bien;
  if (f.estado === 'ambar') return C.ojo;
  if (f.estado === 'rojo') return C.mal;
  return C.tenue;
}

function celdaFraccion(rotulo, f) {
  const color = colorDeFraccion(f);
  const valor = f.estado === 'sin-eventos' ? 'sin eventos' : f.texto;
  return '<div data-cumpl-fraccion="' + esc(f.estado) + '" style="min-width:0">'
    + '<div style="font-size:0.875rem;color:' + C.suave + '">' + rotulo + '</div>'
    + '<div style="font-size:.95rem;font-weight:700;color:' + color + ';font-variant-numeric:tabular-nums">' + esc(valor) + '</div>'
    + '</div>';
}

function bloqueCumplimiento(res) {
  if (!res || !res.ok) {
    return '<div data-cumpl-sin-datos style="background:' + C.panel + ';border:1px solid ' + C.borde + ';border-radius:12px;padding:16px">'
      + '<div style="font-size:0.875rem;color:' + C.texto + ';font-weight:700">Todavia no puedo mostrar el cumplimiento de las reglas</div>'
      + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:6px">' + esc(res?.detalle || '') + '</div>'
      + '<div style="font-size:0.875rem;color:' + C.tenue + ';margin-top:6px">No estan en cero: es que no llego la respuesta.</div></div>';
  }
  const filas = leerCumplimiento(res.data);
  if (!filas.length) {
    return '<div style="background:' + C.panel + ';border:1px solid ' + C.borde + ';border-radius:12px;padding:16px">'
      + '<div style="font-size:0.875rem;color:' + C.suave + '">La API respondio, pero no trae ningun Argy en los ultimos 7 dias.</div></div>';
  }
  return '<div style="font-size:0.875rem;color:' + C.tenue + ';margin-bottom:8px">'
    + 'Ultimos 7 dias. Cada fraccion es cuantos eventos cumplen la regla de cuantos hubo.</div>'
    + '<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:12px">'
    + filas.map(function (f) {
      return '<div data-cumpl-argy="' + esc(f.argy) + '" style="background:' + C.panel + ';border:1px solid ' + C.borde + ';border-radius:12px;padding:12px 14px">'
        + '<div style="font-size:0.875rem;color:' + C.texto + ';font-weight:700;margin-bottom:8px">' + esc(f.argy) + '</div>'
        + '<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px">'
        + celdaFraccion('Cierres con las claves', f.cierres)
        + celdaFraccion('WIP con checklist', f.wips)
        + celdaFraccion('DONE con nodos', f.dones)
        + '</div></div>';
    }).join('')
    + '</div>';
}

/**
 * LOS NUMEROS DE ARQUITECTURA CON SU DUENO (SALA-ARQ-VISTA-T1 partes 1 y 2). Cada tarjeta dice
 * quien lo debe y de cuando es; un vencido sale SIN DATO, nunca el numero viejo en grande.
 * Las reglas viven en application/salud/vigentes.js.
 */
function bloqueVigentes(res) {
  if (!res || !res.ok) {
    const porQue = res?.motivo === 'NO_PUBLICADO'
      ? 'Falta publicar los numeros con dueno (nexo-api PR 429).'
      : 'No pudimos leer los numeros con dueno: ' + esc(res?.detalle || 'sin respuesta') + '.';
    return '<div data-vigentes-motivo="' + esc(res?.motivo || 'SIN_RESPUESTA') + '" style="padding:14px;color:' + C.suave + ';font-size:0.875rem">' + porQue + ' El resto de la Sala sigue igual.</div>';
  }
  const sala = salaDeVigentes(res.data);
  if (!sala.total) {
    return '<div data-vigentes-motivo="VACIO" style="padding:14px;color:' + C.suave + ';font-size:0.875rem">La API respondio sin numeros de arquitectura: la foto del dia todavia no ha corrido.</div>';
  }
  const deben = Object.entries(sala.deben).map(([q, n]) => esc(q) + ' debe ' + n).join(' &middot; ');
  const resumen = '<div data-vigentes-resumen style="grid-column:1/-1;font-size:0.875rem;color:' + (sala.vencidas ? C.mal : C.suave) + '">'
    + sala.vencidas + ' de ' + sala.total + ' vencidos' + (deben ? ': ' + deben : '') + '</div>';
  const COLOR = { vencida: C.mal, sin_dato: C.tenue, vivo: C.frio };
  return resumen + sala.tarjetas.map(function (t) {
    const color = COLOR[t.cara.estado];
    return '<div data-vigente-estado="' + t.cara.estado + '" style="background:' + C.panel + ';border:1px solid ' + C.borde + ';border-left:3px solid ' + color + ';border-radius:10px;padding:13px">'
      + '<div style="display:flex;justify-content:space-between;align-items:baseline;gap:10px">'
      + '<span style="font-size:0.875rem;color:' + C.texto + ';font-weight:600">' + esc(t.titulo) + '</span>'
      + '<span data-vigente-grande style="font-size:1.15rem;color:' + color + ';font-weight:700;font-family:monospace">' + esc(t.cara.grande) + '</span>'
      + '</div>'
      + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:6px;line-height:1.5">' + esc(t.cara.frase) + '</div>'
      + '<div style="font-size:0.875rem;color:' + C.tenue + ';margin-top:4px">' + esc(t.dueno || 'sin dueno') + (t.fuente ? ' &middot; ' + esc(t.fuente) : '') + '</div>'
      + '</div>';
  }).join('');
}

/**
 * LA SALUD DE LOS CLIENTES (SALUD-CLIENTES-T1, ASSIGN de Ares #24364). Arriba las seis fracciones de la casa; abajo
 * un semaforo por negocio, el que mas debe primero. Sin fuente se dice "sin fuente", no se pinta en rojo.
 * Las reglas viven en application/salud/clientes.js.
 */
function bloqueClientes(tablero) {
  const c = leerClientes(tablero);
  if (!c) {
    return '<div data-clientes-motivo="SIN_FOTO" style="grid-column:1/-1;padding:14px;color:' + C.suave + ';font-size:0.875rem">'
      + 'Todavia no hay foto de clientes: la esta preparando la API (SALUD-CLIENTES-T1). El resto de la Sala sigue igual.</div>';
  }
  if (!c.negocios.length) {
    return '<div data-clientes-motivo="VACIO" style="grid-column:1/-1;padding:14px;color:' + C.suave + ';font-size:0.875rem">'
      + 'La foto de clientes llego sin negocios activos.</div>';
  }
  const COLOR = { bien: C.bien, mal: C.mal, 'sin-fuente': C.tenue };
  const resumen = '<div data-clientes-resumen style="grid-column:1/-1;display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:8px">'
    + c.resumen.map(function (r) {
      const e = estadoDelResumen(r);
      return '<div data-clientes-dim="' + esc(r.dimension) + '" style="background:' + C.panel + ';border:1px solid ' + C.borde + ';border-radius:10px;padding:10px 12px">'
        + '<div style="font-size:0.875rem;color:' + C.suave + '">' + esc(r.titulo) + ' al dia</div>'
        + '<div style="font-size:1rem;font-weight:700;color:' + COLOR[e] + ';font-variant-numeric:tabular-nums">' + esc(fraccionDe(r)) + '</div>'
        + (sinDatoDe(r) ? '<div data-clientes-sin-dato style="font-size:0.875rem;color:' + C.tenue + '">' + esc(sinDatoDe(r)) + '</div>' : '')
        + '</div>';
    }).join('') + '</div>';
  const celda = function (x, titulo) {
    return '<td data-clientes-celda="' + esc(x.estado) + '" title="' + esc(titulo + ': ' + x.frase + (x.fuente ? ' (sale de ' + x.fuente + ')' : '')) + '" style="padding:6px 8px;border-bottom:1px solid ' + C.borde + ';white-space:nowrap">'
      + '<span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:' + COLOR[x.estado] + ';margin-right:6px;vertical-align:middle"></span>'
      + '<span style="font-size:0.875rem;color:' + (x.estado === 'mal' ? C.texto : C.suave) + '">' + esc(x.frase) + '</span></td>';
  };
  const tabla = '<div style="grid-column:1/-1;overflow-x:auto;-webkit-overflow-scrolling:touch">'
    + '<table data-clientes-tabla style="width:100%;border-collapse:collapse;font-size:0.875rem">'
    + '<thead><tr><th style="text-align:left;padding:6px 8px;color:' + C.suave + ';font-weight:600;position:sticky;left:0;background:' + C.panel + '">Negocio</th>'
    + DIMENSIONES.map((d) => '<th style="text-align:left;padding:6px 8px;color:' + C.suave + ';font-weight:600" title="' + esc(d.pregunta) + '">' + esc(d.titulo) + '</th>').join('')
    + '</tr></thead><tbody>'
    + c.negocios.map(function (n) {
      return '<tr data-clientes-negocio="' + esc(n.id) + '">'
        + '<td style="padding:6px 8px;border-bottom:1px solid ' + C.borde + ';color:' + C.texto + ';font-weight:600;position:sticky;left:0;background:' + C.panel + '">'
        + esc(n.nombre) + (n.deben ? ' <span style="font-size:0.875rem;color:' + C.mal + ';font-weight:400">(' + n.deben + ')</span>' : '') + '</td>'
        + DIMENSIONES.map((d) => celda(n.celdas[d.clave], d.titulo)).join('')
        + '</tr>';
    }).join('')
    + '</tbody></table></div>';
  const pie = '<div style="grid-column:1/-1;font-size:0.875rem;color:' + C.tenue + '">Foto del ' + esc(c.fecha || 'dia sin fecha')
    + '. Gris = no hay de donde sacar el dato todavia (no es que este mal). Pasa el mouse por una celda para ver de donde sale.</div>';
  return resumen + tabla + pie;
}

export async function renderSalud(container) {
  const $el = container;

  $el.innerHTML = '<div class="detail-section">'
    + '<div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px">'
    + ('<div>' + regresoHtml())
    + '<h1 style="margin:0;font-size:1.1rem;color:' + C.texto + '">&#9881;&#65039; Sala de Maquinas</h1>'
    + '<p style="font-size:0.875rem;color:' + C.suave + ';margin-top:4px">Esto de verdad esta funcionando, o solo lo parece?</p></div>'
    + '<a href="#/agents-dashboard" class="btn-ghost btn-sm" style="font-size:0.875rem;text-decoration:none">&#128101; Ver estado live &rarr;</a>'
    + '</div></div>'
    // SALUD-PANEL-T1: los chequeos van primero y aparte del tablero (si uno falla, el otro se pinta igual).
    + '<div data-salud-chequeos-host></div>'
    // Ares #38145 paso 4: las capas tambien van aparte del tablero.
    + '<div data-salud-capas-host></div>'
    + '<div data-salud-cuerpo style="margin-top:16px">'
    + '<div style="padding:30px;text-align:center;color:' + C.suave + ';font-size:0.875rem">'
    + '<div style="font-size:1.6rem;margin-bottom:8px">&#9881;&#65039;</div>Midiendo el sistema...</div></div>';

  const $cuerpo = $el.querySelector('[data-salud-cuerpo]');
  renderChequeosSalud($el.querySelector('[data-salud-chequeos-host]')).catch(() => {});
  // Ares #38227: una sola peticion de capas para la grilla y el dial.
  const capasP = SaludApiClient.getCapas().catch(() => ({ ok: false, motivo: 'ERROR_API' }));
  renderCapasArquitectura($el.querySelector('[data-salud-capas-host]'), capasP).catch(() => {});
  // Las dos preguntas van juntas y por separado: el tablero puede estar vivo
  // y los indicadores no (o al reves). allSettled para que el fallo de uno
  // no borre al otro de la pantalla.
  const [res, resInd, resCumpl, resVig] = await Promise.all([
    SaludApiClient.getTablero(),
    SaludApiClient.getIndicadores().catch((e) => ({ ok: false, motivo: 'ERROR_PANEL', detalle: e?.message || 'fallo la consulta' })),
    SaludApiClient.getCumplimientoArgys().catch((e) => ({ ok: false, motivo: 'ERROR_PANEL', detalle: e?.message || 'fallo la consulta' })),
    SaludApiClient.getVigentes().catch((e) => ({ ok: false, motivo: 'ERROR_PANEL', detalle: e?.message || 'fallo la consulta' }))
  ]);

  if (!res.ok) {
    $cuerpo.innerHTML = pantallaSinDatos(res.motivo, res.detalle);
    return;
  }

  const d = res.data;
  function seccion(t, contenido, cols) {
    return '<div style="margin-top:24px">'
      + '<div style="font-size:0.875rem;color:' + C.neon + ';font-weight:700;letter-spacing:.06em;text-transform:uppercase;margin-bottom:10px">' + t + '</div>'
      + '<div style="display:grid;grid-template-columns:' + (cols || 'repeat(auto-fit,minmax(280px,1fr))') + ';gap:12px">' + contenido + '</div></div>';
  }

  const sinAgentes = '<div style="color:' + C.suave + ';font-size:0.875rem">No llegaron datos de agentes.</div>';

  $cuerpo.innerHTML = reactores(d, await capasP)
    + seccion('Clientes: quien esta al dia y quien no', bloqueClientes(d), '1fr')
    + seccion('Como vamos: los seis numeros del trabajo', bloqueLosSeis(resInd), '1fr')
    + seccion('Reglas v2: quien las cumple', bloqueCumplimiento(resCumpl), '1fr')
    + seccion('Arquitectura con dueno: cada numero dice quien lo debe', bloqueVigentes(resVig), 'repeat(auto-fit,minmax(240px,1fr))')
    + seccion('Agente por agente', (d.agentes || []).map(filaAgente).join('') || sinAgentes)
    + seccion('Arquitectura', avisoDeVencido(d.foto) + bloqueArquitectura(d.arquitectura || []), 'repeat(auto-fit,minmax(240px,1fr))')
    + seccion('Tendencia', bloqueTendencia(d.tendencia || []), 'repeat(auto-fit,minmax(240px,1fr))')
    // SALA-ARQ-VISTA-T1: el pie dice la fecha del dato MAS VIEJO de lo que se pinta, no la hora de la pantalla.
    + (() => { const m = medidoDelPie(d); return '<div data-medido-pie style="margin-top:20px;font-size:0.875rem;color:' + C.tenue + ';text-align:right">Medido '
      + (m ? esc(m.fecha) + ' (el dato mas viejo: ' + esc(m.de) + ')' : 'sin marca de tiempo') + '</div>'; })();
}

/** Encima de la seccion Arquitectura: si la foto esta vencida, se dice antes de los numeros (ocupa toda la fila). */
function avisoDeVencido(foto) {
  const dias = diasDeLaArquitectura(foto);
  if (estadoDeFrescura(dias) !== 'vencido') return '';
  return '<div data-arq-vencido style="grid-column:1/-1;padding:10px 12px;border:1px solid ' + C.mal + ';border-radius:8px;color:' + C.mal + ';font-size:0.875rem;font-weight:700">'
    + 'VENCIDO: estos numeros son de ' + esc(String(foto?.arquitectura || '').slice(0, 10)) + ' (hace ' + dias + ' dias). No describen el sistema de hoy: falta que alguien vuelva a tomar la foto.</div>';
}
